import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { apiChangePassword, formatApiError, toastApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ChangePasswordPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (next !== confirm) {
      setErr("New password and confirmation must match.");
      return;
    }
    setBusy(true);
    try {
      await apiChangePassword({ current_password: cur, new_password: next });
      toast.success("Password updated");
      nav("/portfolios", { replace: true });
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
          Account
        </div>
        <h1 className="mt-1 text-2xl text-[var(--aurion-text)]">Change password</h1>
        <p className="mt-1 text-xs text-[var(--aurion-muted)]">
          Signed in as <span className="font-mono">{user?.email}</span>. Minimum 10 characters
          with at least one letter and one digit; common passwords are rejected.
        </p>
      </div>
      <Card
        data-testid="change-password-card"
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-8"
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cur-pw" className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
              Current password
            </Label>
            <Input
              id="cur-pw"
              data-testid="change-pw-current"
              type="password"
              autoComplete="current-password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              required
              className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
            />
          </div>
          <div>
            <Label htmlFor="new-pw" className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
              New password (min 10)
            </Label>
            <Input
              id="new-pw"
              data-testid="change-pw-new"
              type="password"
              autoComplete="new-password"
              minLength={10}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pw" className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
              Confirm new password
            </Label>
            <Input
              id="confirm-pw"
              data-testid="change-pw-confirm"
              type="password"
              autoComplete="new-password"
              minLength={10}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
            />
          </div>
          {err ? (
            <div data-testid="change-pw-error" className="text-sm text-red-400">
              {err}
            </div>
          ) : null}
          <Button
            data-testid="change-pw-submit"
            type="submit"
            disabled={busy || !cur || !next || !confirm}
            className="h-11 w-full border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 font-semibold tracking-widest text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Update password
          </Button>
        </form>
      </Card>
    </div>
  );
}
